import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { passcodeLogin, getMe, type ApiError } from '../lib/api';

/** Friendly bilingual copy for each ?sso_error= code the callback can bounce back with. */
const SSO_ERROR_MSG: Record<string, string> = {
  no_access: '你的企业微信账号未被授权访问本系统，请联系管理员。 / Your WeCom account isn’t authorized for this system — contact an admin.',
  pending: '已收到你的访问请求，等待管理员开通后即可登录。 / Your access request is recorded — you can sign in once an admin approves it.',
  disabled: '企业微信登录暂未启用。 / WeCom sign-in is not enabled yet.',
  state: '登录会话已过期，请重新登录。 / Your sign-in session expired — please try again.',
  exchange: '企业微信登录失败，请重试。 / WeCom sign-in failed — please try again.',
  verify: '无法校验企业微信登录，请重试。 / Could not verify the WeCom sign-in — please try again.',
  server: '登录出错，请重试。 / Something went wrong signing in — please try again.',
};

/** DR-A · passcode login for the intranet dry run + WeCom (企业微信) SSO for staff.
 *  On success the session cookie is set; staff land on the dashboard, labor on their portal. */
export default function Login() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe });
  const [loginId, setLoginId] = useState('');
  const [passcode, setPasscode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ssoError = params.get('sso_error');
  const ssoErrorMsg = ssoError ? (SSO_ERROR_MSG[ssoError] ?? SSO_ERROR_MSG.server) : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!loginId.trim() || !passcode) return;
    setBusy(true); setErr(null);
    try {
      const r = await passcodeLogin(loginId.trim(), passcode);
      await qc.invalidateQueries();
      nav(r.account?.role === 'labor_co' ? '/portal' : '/', { replace: true });
    } catch (e) {
      setErr((e as ApiError)?.status === 401 ? '账号或密码不正确 / wrong login or passcode' : ((e as ApiError)?.message ?? '登录失败 / login failed'));
    } finally { setBusy(false); }
  }

  return (
    <div style={{ maxWidth: 360, margin: '8vh auto' }}>
      <div className="card enter-pop">
        <h2 style={{ marginTop: 0 }}>登录 / Sign in</h2>
        <p className="muted small">劳务承包平台 · Labor Procurement</p>

        {ssoErrorMsg && (
          <div className="small" style={{ color: 'var(--bad)', margin: '8px 0', lineHeight: 1.4 }}>{ssoErrorMsg}</div>
        )}

        {me?.sso_enabled && (
          <>
            <a
              href="/api/auth/sso/login"
              className="btn-primary"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none', background: '#07C160', borderColor: '#07C160', marginBottom: 14 }}
            >
              企业微信登录 / Sign in with WeCom
            </a>
            {me?.passcode_login_enabled !== false && (
              <div className="small muted" style={{ textAlign: 'center', margin: '6px 0 12px' }}>或 / or</div>
            )}
          </>
        )}

        {/* WeCom-only cutover: when passcode login is retired server-side, don't show a form that can only fail. */}
        {me?.passcode_login_enabled !== false ? (
          <form onSubmit={submit}>
            <label className="small muted">账号 / Login ID</label>
            <input autoFocus value={loginId} onChange={(e) => setLoginId(e.target.value)} placeholder="账号 / login id" style={{ width: '100%', marginBottom: 10 }} />
            <label className="small muted">密码 / Passcode</label>
            <input type="password" value={passcode} onChange={(e) => setPasscode(e.target.value)} style={{ width: '100%', marginBottom: 10 }} />
            {err && <div className="small" style={{ color: 'var(--bad)', marginBottom: 8 }}>{err}</div>}
            <button className="btn-primary" disabled={busy || !loginId.trim() || !passcode} style={{ width: '100%' }}>{busy ? '登录中… / Signing in…' : '登录 / Sign in'}</button>
          </form>
        ) : (
          <p className="small muted" style={{ textAlign: 'center', marginTop: 4 }}>
            员工请使用企业微信扫码登录；劳务公司请<Link to="/portal/login">用手机号登录 →</Link>
            <br />Staff sign in with WeCom; labor companies <Link to="/portal/login">sign in by phone →</Link>
          </p>
        )}
        <div className="small muted" style={{ marginTop: 10 }}>
          劳务公司首次使用？ / First-time labor co? <Link to="/portal/login">用认领链接 / use a claim link →</Link>
        </div>
      </div>
    </div>
  );
}
